import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, TemplateRef, ViewChild } from '@angular/core';
import { CurrentUrlService, RestService, stripQueryParams } from '@cumlaude/shared-services';
import { BehaviorSubject, combineLatest, firstValueFrom, lastValueFrom, Observable, switchMap } from 'rxjs';
import { FavorietDialogComponent, FavorietDialogResultaat } from '../dialogs/favorieten/favoriet-dialog/favoriet-dialog.component';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { fromPairs, groupBy, omit, sortBy } from 'lodash-es';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { Dialog } from '@angular/cdk/dialog';
import { NotifiableError } from '@bugsnag/js';
import { BugsnagService } from '@cumlaude/bugsnag';
import { AsyncPipe } from '@angular/common';
import { ConfirmDialogComponent } from '@cumlaude/shared-components-dialogs';
import { MeatballMenuComponent, MeatballMenuItemComponent } from '@cumlaude/shared-components-menu';
import { ImmediateInputComponent } from '../shared/components/immediate-input/immediate-input.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { UserService } from '../services/user.service';
import { DashboardNavigatieService } from '../services/dashboard-navigatie.service';
import { GtmService } from '../services/gtm.service';
import { getRolName, RFavoriet, RFavorietMap, RRol } from '@cumlaude/service-contract';
import { AutorisatieService } from '../services/autorisatie.service';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { findDashboardLeaf } from '@cumlaude/shared-dashboard-registry';

type Group = {
	map?: RFavorietMap;
	items: Favoriet[];
};

type Favoriet = RFavoriet & {
	allowedRollen: RRol[];
	hoofdpagina?: string;
};

@Component({
	selector: 'app-favorieten-list',
	templateUrl: './favorieten-list.component.html',
	styleUrls: ['./favorieten-list.component.scss'],
	host: {
		'[class.scrollable]': 'scrollable()',
	},
	imports: [
		CdkDropListGroup,
		CdkScrollable,
		CdkDropList,
		ImmediateInputComponent,
		MeatballMenuComponent,
		MeatballMenuItemComponent,
		CdkDrag,
		CdkDragPlaceholder,
		ConfirmDialogComponent,
		AsyncPipe,
		TooltipDirective,
		IconComponent,
		ButtonComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavorietenListComponent {
	scrollable = input(false);

	/** Emit wanneer op een favoriet genavigeerd wordt, zodat een pop-out die deze lijst toont zichzelf kan sluiten. */
	favorietGeklikt = output<void>();

	private readonly restService = inject(RestService);
	protected readonly userService = inject(UserService);
	private readonly autorisatieService = inject(AutorisatieService);
	private readonly dialog = inject(Dialog);
	protected readonly router = inject(Router);
	protected readonly toastr = inject(ToastrService);
	protected readonly bugsnag = inject(BugsnagService);
	private readonly dashboardNavigatie = inject(DashboardNavigatieService);
	private readonly currentUrlService = inject(CurrentUrlService);
	private readonly gtm = inject(GtmService);

	@ViewChild('deleteMapDialog')
	deleteMapDialog!: TemplateRef<{ data: Group }>;

	@ViewChild('deleteFavorietDialog')
	deleteFavorietDialog!: TemplateRef<{ data: RFavoriet }>;

	groups$!: Observable<Group[]>;

	tempGroup$ = new BehaviorSubject<Group[]>([]);

	opened = new Set<string>([]);

	dragActive = false;

	// undefined = nieuwe map, null = not editing
	editingMap = signal<string | undefined | null>(null);

	refreshFavorieten$ = new BehaviorSubject<void>(undefined);

	tooltip = computed(() => {
		const activeRol = this.userService.activeRol();
		return activeRol ? `Niet beschikbaar voor je huidige rol (${getRolName(activeRol)})` : 'Niet beschikbaar zonder actieve rol';
	});

	toonFavorietKnop = computed(() => this.currentUrlService.currentPath() !== '/start');

	constructor() {
		this.groups$ = this.refreshFavorieten$.pipe(
			switchMap(() => combineLatest([this.restService.getFavorieten(), this.restService.getFavorietMappen(), this.tempGroup$])),
			map(([favorieten, mappen, tempGroup]) => this.makeGroups(favorieten, mappen, tempGroup))
		);
	}

	toggleMapOpen(mapId: string | undefined) {
		if (mapId === undefined) return;

		if (this.opened.has(mapId)) this.opened.delete(mapId);
		else this.opened.add(mapId);
	}

	openMap(mapId: string | undefined) {
		if (mapId === undefined || this.opened.has(mapId)) return;

		this.opened.add(mapId);
	}

	async nieuweFavoriet() {
		const groups = await firstValueFrom(this.groups$);
		await this.openFavorietDialog(groups);
	}

	async openFavorietDialog(groups: Group[], favoriet?: Favoriet) {
		const dialogRef = this.dialog.open<FavorietDialogResultaat>(FavorietDialogComponent, {
			data: {
				template: await this.getNieuweMapTemplate(),
				mappen: groups.map((g) => g.map).filter((m) => m !== undefined),
				url: this.router.url,
				favoriet: favoriet && omit(favoriet, ['allowedRollen', 'hoofdpagina']),
			},
		});
		const result = await lastValueFrom(dialogRef.closed);
		if (!result) return;

		const { favoriet: opgeslagenFavoriet, nieuweMappen } = result;

		try {
			const persistedNieuweMappen = await lastValueFrom(this.restService.postFavorietMappen(nieuweMappen));
			const nieuweMapIndex = nieuweMappen.findIndex((m) => m === opgeslagenFavoriet.map);
			if (nieuweMapIndex >= 0) opgeslagenFavoriet.map = persistedNieuweMappen[nieuweMapIndex];

			if (opgeslagenFavoriet.id) await lastValueFrom(this.restService.putFavoriet(opgeslagenFavoriet));
			else {
				await lastValueFrom(this.restService.postFavoriet(opgeslagenFavoriet));
				this.gtm.pushEvent('favoriet_aanmaken', { bestemming: stripQueryParams(opgeslagenFavoriet.state), startpunt: 'favorieten_lijst' });
			}

			if (opgeslagenFavoriet.map) this.opened.add(opgeslagenFavoriet.map.id!);
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error(`Er ging iets fout bij het wegschrijven.`);
		}

		this.refreshFavorieten$.next();
	}

	async createOrUpdateFavorietMap(favorietMap: RFavorietMap, naam: string | undefined) {
		this.editingMap.set(null);
		if (!favorietMap.id) this.tempGroup$.next([]);
		if (naam === undefined || naam.trim() === '') return;

		try {
			if (favorietMap.id) {
				await lastValueFrom(this.restService.putFavorietMap({ ...favorietMap, naam }));
			} else {
				await lastValueFrom(this.restService.postFavorietMap({ ...favorietMap, naam }));
			}
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error(`Er ging iets fout bij het wegschrijven.`);
		}

		this.refreshFavorieten$.next();
	}

	async createFavorietMapInput() {
		const nieuweMap = await this.getNieuweMapTemplate();
		this.tempGroup$.next([{ map: nieuweMap, items: [] }]);
		this.editingMap.set(undefined);
	}

	async getNieuweMapTemplate(): Promise<RFavorietMap> {
		const account = await firstValueFrom(this.userService.myAccount$);
		return { $type: 'favoriet.RFavorietMap', naam: 'Nieuwe map', account, instelling: account.instelling };
	}

	async confirmDeleteMap(group: Group) {
		const dialogRef = this.dialog.open(this.deleteMapDialog, {
			data: group,
		});
		const result = await lastValueFrom(dialogRef.closed);
		if (!result) return;

		try {
			await lastValueFrom(this.restService.deleteFavorietMap(group.map!));
			this.toastr.success(`Map ${group.map!.naam} verwijderd.`);
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error(`Er ging iets fout bij het verwijderen.`);
		}
		this.refreshFavorieten$.next();
	}

	async confirmDeleteFavoriet(favoriet: RFavoriet) {
		const dialogRef = this.dialog.open(this.deleteFavorietDialog, {
			data: favoriet,
		});
		const result = await lastValueFrom(dialogRef.closed);
		if (!result) return;

		try {
			await lastValueFrom(this.restService.deleteFavoriet(favoriet));
			this.toastr.success(`Favoriet ${favoriet.naam} verwijderd.`);
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error(`Er ging iets fout bij het verwijderen.`);
		}
		this.refreshFavorieten$.next();
	}

	openFavoriet(favoriet: Favoriet) {
		if (this.dragActive) return;

		if (!this.favorietClickable(favoriet.allowedRollen)) return;

		const bestemming = favoriet.state;
		this.dashboardNavigatie.track('favoriet', bestemming);
		this.router.navigateByUrl(bestemming);
		this.favorietGeklikt.emit();
	}

	async drop(event: CdkDragDrop<Group, Group, RFavoriet>) {
		this.dragActive = false;

		if (event.container === event.previousContainer) return;

		const favorietMap = event.container.data.map;
		this.openMap(favorietMap?.id);

		const favoriet = event.item.data;
		try {
			await lastValueFrom(this.restService.putFavoriet({ ...omit(favoriet, ['allowedRollen', 'hoofdpagina']), map: favorietMap }));
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error(`Er ging iets fout bij het verplaatsen van de favoriet.`);
		}

		this.refreshFavorieten$.next();
	}

	/**
	 * Groepeert favorieten per map.
	 * Houdt rekening met lege mappen, en een "map: undefined" groep voor de root.
	 * Sorteert mappen en favorieten op naam.
	 */
	makeGroups(favorieten: RFavoriet[], mappen: RFavorietMap[], tempGroup: Group[]): Group[] {
		// groepeer root-group onder ''
		const favorietenPerMapId: { [id: string]: Favoriet[] } = groupBy(
			favorieten.map((favoriet) => ({
				...favoriet,
				allowedRollen: this.getAllowedRollen(favoriet),
				hoofdpagina: findDashboardLeaf(stripQueryParams(favoriet.state))?.hoofdpagina,
			})),
			(favoriet) => favoriet.map?.id ?? ''
		);

		const mapPerId: { [id: string]: RFavorietMap } = fromPairs(mappen.map((m) => [m.id, m]));
		const mapIds = Object.keys(mapPerId);
		const mapIdsSorted = [...sortBy(mapIds, (k) => mapPerId[k].naam.toLowerCase()), '']; // root-group onderaan

		const groups = mapIdsSorted.map((mapId) => ({
			map: mapPerId[mapId], // root-group krijgt hier undefined
			items: sortBy(favorietenPerMapId[mapId] ?? [], (f) => f.naam.toLowerCase()),
		}));

		return [...tempGroup, ...groups];
	}

	private getAllowedRollen(favoriet: RFavoriet): RRol[] {
		const rollen = this.userService.rollen();
		if (!rollen) return [];

		const allowedRollen = [];
		for (const rol of rollen) {
			if (this.autorisatieService.isUrlAllowedForRollen(favoriet.state, [rol])) allowedRollen.push(rol);
		}
		return allowedRollen;
	}

	favorietClickable(allowedRollen: RRol[]): boolean {
		const activeRol = this.userService.activeRol();
		if (!activeRol) return false;

		return allowedRollen.includes(activeRol);
	}
}
